// ============================================================================
// OPTIMIZED HR NOTIFICATION SERVICE - PERFORMANCE ENHANCED
// frontend/src/services/hrNotification.service.ts
// ============================================================================

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
  Unsubscribe,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';

export interface HRNotification {
  id: string;
  applicationId: string;
  employeeId: string;
  employeeName: string;
  employeePin: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  reason?: string;
  lineManagerName: string;
  status: 'approved' | 'rejected' | 'pending' | 'overdue';
  comments?: string;
  approvedBy?: string;
  type: 'leave_status_update' | 'leave_pending_overdue';
  action: string;
  priority: 'normal' | 'high';
  isRead: boolean;
  title: string;
  message: string;
  hasCertificate: boolean;
  certificateUrl?: string;
  createdAt: string;
  applicationDate: string;
  category: string;
  source: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  high: number;
  today: number;
  thisWeek: number;
}

class HRNotificationService {
  private activeListeners = new Set<Unsubscribe>();
  private _notificationsCache: { data: HRNotification[]; timestamp: number } | null = null;
  private readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutes

  private formatFirebaseDateTime(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  }

  private formatFirebaseDate(timestamp: any): string {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-CA');
    } catch {
      return '';
    }
  }

  private processNotification(doc: any): HRNotification {
    const data = doc.data();
    
    return {
      id: doc.id,
      applicationId: data.applicationId || '',
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || 'Unknown Employee',
      employeePin: data.employeePin || '',
      leaveType: data.leaveType || '',
      startDate: this.formatFirebaseDate(data.startDate),
      endDate: this.formatFirebaseDate(data.endDate),
      totalDays: data.totalDays || 0,
      reason: data.reason || '',
      lineManagerName: data.lineManagerName || '',
      status: data.status || 'pending',
      comments: data.comments || '',
      approvedBy: data.approvedBy || '',
      type: data.type || 'leave_status_update',
      action: data.action || '',
      priority: data.priority || 'normal',
      isRead: data.isRead || false,
      title: data.title || '',
      message: data.message || '',
      hasCertificate: data.hasCertificate || false,
      certificateUrl: data.certificateUrl || '',
      createdAt: this.formatFirebaseDateTime(data.createdAt),
      applicationDate: this.formatFirebaseDate(data.applicationDate),
      category: data.category || 'leave_management',
      source: data.source || 'unknown'
    };
  }

  async getRecentNotifications(limitCount: number = 20): Promise<HRNotification[]> {
    try {
      // Check cache
      if (this._notificationsCache && Date.now() - this._notificationsCache.timestamp < this.CACHE_TTL) {
        return this._notificationsCache.data.slice(0, limitCount);
      }

      const notificationsRef = collection(db, 'hr_notifications');
      const notificationsQuery = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(notificationsQuery);
      const notifications = snapshot.docs.map(doc => this.processNotification(doc));

      // Cache the results
      this._notificationsCache = { data: notifications, timestamp: Date.now() };

      return notifications;

    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch notifications');
    }
  }

  async getNotificationStats(): Promise<NotificationStats> {
    try {
      const notifications = await this.getRecentNotifications(100);
      
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - 7);

      return {
        total: notifications.length,
        unread: notifications.filter(n => !n.isRead).length,
        high: notifications.filter(n => n.priority === 'high').length,
        today: notifications.filter(n => {
          const notifDate = new Date(n.createdAt);
          return notifDate >= todayStart;
        }).length,
        thisWeek: notifications.filter(n => {
          const notifDate = new Date(n.createdAt);
          return notifDate >= weekStart;
        }).length
      };

    } catch {
      return { total: 0, unread: 0, high: 0, today: 0, thisWeek: 0 };
    }
  }

  onNotifications(
    callback: (notifications: HRNotification[]) => void,
    onError?: (error: Error) => void,
    limitCount: number = 50
  ): Unsubscribe {
    const notificationsRef = collection(db, 'hr_notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        try {
          const notifications = snapshot.docs.map(doc => this.processNotification(doc));
          
          // Update cache
          this._notificationsCache = { data: notifications, timestamp: Date.now() };
          
          callback(notifications);
        } catch (error) {
          onError?.(error instanceof Error ? error : new Error('Real-time listener error'));
        }
      },
      (error) => {
        onError?.(error);
      }
    );

    this.activeListeners.add(unsubscribe);
    return unsubscribe;
  }

  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const notificationRef = doc(db, 'hr_notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        readAt: Timestamp.now()
      });

      // Invalidate cache
      this._notificationsCache = null;

      return true;
    } catch {
      return false;
    }
  }

  async markAllAsRead(): Promise<boolean> {
    try {
      const unreadNotifications = await this.getUnreadNotifications();
      
      const updatePromises = unreadNotifications.map(notification => 
        this.markAsRead(notification.id)
      );

      await Promise.all(updatePromises);
      return true;
    } catch {
      return false;
    }
  }

  async getUnreadNotifications(): Promise<HRNotification[]> {
    try {
      const notificationsRef = collection(db, 'hr_notifications');
      const unreadQuery = query(
        notificationsRef,
        where('isRead', '==', false),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(unreadQuery);
      return snapshot.docs.map(doc => this.processNotification(doc));
    } catch {
      return [];
    }
  }

  getNotificationIcon(notification: HRNotification): string {
    if (notification.type === 'leave_pending_overdue') return '⏰';
    if (notification.action === 'approved') return '✅';
    if (notification.action === 'rejected') return '❌';
    return '📋';
  }

  getNotificationColor(notification: HRNotification): string {
    if (notification.priority === 'high') return '#ef4444';
    if (notification.action === 'approved') return '#10b981';
    if (notification.action === 'rejected') return '#f59e0b';
    return '#6b7280';
  }

  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const notificationsRef = collection(db, 'hr_notifications');
      const testQuery = query(notificationsRef, limit(1));
      await getDocs(testQuery);
      
      return {
        success: true,
        message: 'HR notifications connection successful'
      };
    } catch (error) {
      return {
        success: false,
        message: `HR notifications connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  clearCache(): void {
    this._notificationsCache = null;
  }

  cleanup(): void {
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
    this._notificationsCache = null;
  }
}

export const hrNotificationService = new HRNotificationService();